<script setup lang="ts">
/**
 * CaInput — membungkus <UInput>.
 * Aturan keras design system: font-size MINIMUM 16px dan tinggi 48px.
 * Ini yang memperbaiki temuan audit #5: input lama 15.2px membuat
 * Android/iOS melakukan zoom paksa tiap kali kolom disentuh — terasa seperti situs rusak.
 */
withDefaults(
  defineProps<{
    modelValue?: string
    type?: string
    placeholder?: string
    icon?: string
    disabled?: boolean
    /** Untuk kode aktivasi: monospace + huruf besar otomatis. */
    mono?: boolean
    inputmode?: string
    autocomplete?: string
    id?: string
    /**
     * Hubungan a11y yang diberikan CaField lewat slot prop. Dijadikan prop
     * eksplisit — bukan mengandalkan attribute fallthrough — karena root
     * komponen ini adalah <UInput> yang memakai `inheritAttrs: false`; menaruh
     * atribut aria di sana berarti menyerahkan nasibnya ke detail internal
     * library.
     */
    describedby?: string
    invalid?: boolean
  }>(),
  { type: 'text' }
)
defineEmits<{ 'update:modelValue': [string] }>()
</script>

<template>
  <UInput
    :id="id"
    :model-value="modelValue"
    :type="type"
    :placeholder="placeholder"
    :icon="icon"
    :disabled="disabled"
    :autocomplete="autocomplete"
    :inputmode="inputmode"
    :aria-describedby="describedby"
    :aria-invalid="invalid ? 'true' : undefined"
    class="w-full"
    :ui="{
      root: 'w-full',
      // Di Nuxt UI versi ini, <input> adalah slot `base`. Padding kiri untuk
      // leading icon HARUS di sini (bukan wrapper) dan dipaksa `!important`
      // supaya menang atas default `ps-9` bawaan library — kalau tidak, teks/
      // placeholder menabrak icon. text-base! (16px) cegah zoom paksa Android.
      base: [
        'w-full min-h-12 rounded-control bg-page border border-line text-ink',
        'text-base! placeholder:text-muted',
        icon ? '!ps-11' : 'ps-3.5',
        'pe-3.5',
        mono ? 'font-mono tracking-[0.14em] uppercase' : '',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-ring-ca)]'
      ].join(' '),
      leadingIcon: 'text-muted'
    }"
    @update:model-value="(v: any) => $emit('update:modelValue', String(v))"
  />
</template>
